import {
  FileStatus,
  FormInput,
  OnUpload,
  type UploadingFile,
} from '@diligentcorp/heracles-form-input';
import { MinimalUserDto } from '@issuemanager/api-client';
import {
  useAttachmentsContext,
  useUserContext,
} from '@issuemanager/apps/client/contexts';
import { type WithTheme } from '@issuemanager/apps/client/theme';
import { Stack, styled, type StackProps } from '@mui/material';
import React, { JSX, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserDateRenderer } from '../../hooks';

export type ListFile = {
  id?: string;
  hbDriveId: string;
  name: string;
  size: number;
  createdAt: Date;
  addedBy: MinimalUserDto;
};

const isUploadingFile = (
  file: UploadingFile | ListFile,
): file is UploadingFile => 'file' in file;

type Props = {
  value: (UploadingFile | ListFile)[];
  onChange: (value: (UploadingFile | ListFile)[]) => void;
  fullWidth: boolean;
  initialFileList?: ListFile[];
  disabled?: boolean;
  label: string;
  marginRight?: string;
  hasUploadValidationError?: () => Promise<boolean>;
  name: string;
  hideDeleteButton?: boolean;
};

const StyledStack = styled(Stack, {
  shouldForwardProp: (prop) =>
    prop !== 'fullWidth' && prop !== 'hideDeleteButton',
})<
  WithTheme<
    StackProps & {
      fullWidth: boolean;
      marginRight?: string;
      hideDeleteButton?: boolean;
    }
  >
>(({ theme, fullWidth, marginRight, hideDeleteButton = false }) => ({
  '& [data-testid="heracles-file-upload-tooltip-icon"]': {
    display: 'none',
  },

  '& [data-testid="heracles-form-input-file-upload-box"]': {
    width: fullWidth ? '100%' : '50%',
  },

  '&& [data-testid="heracles-file-upload-metadata"]': {
    marginLeft: theme.spacing(4),
    marginTop: 0,
    display: 'flex',
    alignItems: 'baseline',

    '& [data-testid*="avatar"]': {
      display: 'none',
    },
  },

  // Hide remove button only for files WITH metadata when hideDeleteButton is true
  ...(hideDeleteButton && {
    '& .file-item:has([data-testid="heracles-file-upload-metadata"]) [data-testid="heracles-uploaded-file-remove-button"]':
      {
        display: 'none',
      },
  }),

  '.upload-box': {
    marginRight: marginRight ?? 0,
  },

  '.files-list': {
    display: 'grid',
    gridTemplateColumns: fullWidth ? '1fr' : '1fr 1fr',
    columnGap: fullWidth ? 0 : theme.spacing(4),

    '.file-item': {
      height: '100%',
      position: 'relative',
      minHeight: theme.spacing(11),

      '.file-icon': {
        maxWidth: theme.spacing(4),
        marginTop: theme.spacing(3),

        '.file-link': {
          position: 'absolute',
        },
      },

      '.MuiGrid-item': {
        '&:empty': {
          display: 'none',
        },
      },

      '.uploading': {
        maxWidth: '100%',
        flexGrow: 1,
      },
    },
  },
}));

export function FileUploader({
  value,
  onChange,
  fullWidth,
  initialFileList = [],
  marginRight,
  hasUploadValidationError,
  name,
  hideDeleteButton,
  ...rest
}: Props): JSX.Element {
  const filesRef = useRef<(UploadingFile | ListFile)[]>([]);
  const formInputRef = useRef<HTMLDivElement>(null);
  const { data: userData } = useUserContext();
  const { t } = useTranslation();
  const { uploadAttachments, deleteAttachment } = useAttachmentsContext();
  const { renderDate } = useUserDateRenderer();

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Trigger the file input click
      const fileInput = formInputRef.current?.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }
  };

  const handleDelete = async (
    file: ListFile,
  ): Promise<{
    status: FileStatus;
    errorMessage?: string;
  }> => {
    try {
      if (file.id) {
        await deleteAttachment({ id: file.id });
      }

      const updatedValue = filesRef.current.filter((item) =>
        isUploadingFile(item)
          ? item.file.name !== file.name
          : item.name !== file.name,
      );

      setTimeout(() => {
        onChange(updatedValue);
      }, 250);

      return { status: FileStatus.Success };
    } catch (error) {
      return {
        status: FileStatus.Error,
        errorMessage: t('attachments.deleteErrorMessage'),
      };
    }
  };

  const getSanitizedUniqueFileName = (
    originalName: string,
    otherFileNames: Set<string>,
  ): string => {
    const baseName = originalName.replace(/\(\d+\)$/, '').trim();
    let counter = 1;
    let newName = originalName;

    while (otherFileNames.has(newName)) {
      newName = `${baseName} (${counter})`;
      counter += 1;
    }
    return newName;
  };

  const generateUniqueFileNames = (files: UploadingFile[]): UploadingFile[] => {
    const existingNames = new Set(
      filesRef.current.map((f) => (isUploadingFile(f) ? f.file.name : f.name)),
    );

    const renamedFiles = files.map((file) => {
      const newName = getSanitizedUniqueFileName(file.file.name, existingNames);
      existingNames.add(newName);
      return {
        ...file,
        file: new File([file.file], newName, { type: file.file.type }),
      };
    });
    return renamedFiles;
  };

  const handleUpload: OnUpload = async (files: UploadingFile[]) => {
    if (!userData) return [];
    if (await hasUploadValidationError?.()) {
      return [];
    }

    const renamedFiles = generateUniqueFileNames(files);
    const attachments = await uploadAttachments(renamedFiles);

    const newFiles = attachments.map((attachment) => ({
      addedBy: userData,
      createdAt: new Date(),
      hbDriveId: attachment.hbDriveId,
      name: attachment.name,
      size: attachment.size,
      tempId: attachment.tempId,
      status: attachment.status,
    }));

    onChange([
      ...filesRef.current,
      ...newFiles.filter((file) => file.status === FileStatus.Success),
    ]);

    return newFiles.map((attachment) => ({
      id: attachment.hbDriveId,
      tempId: attachment.tempId,
      displayName: attachment.name,
      size: attachment.size,
      status: attachment.status,
      errorMessage: t('attachments.uploadErrorMessage'),
      onDelete: () => handleDelete(attachment),
    }));
  };

  useEffect(() => {
    // Need to keep track of the files in a ref because it is always empty in handleDelete
    filesRef.current = value;
  }, [value]);

  return (
    <StyledStack
      fullWidth={fullWidth}
      marginRight={marginRight}
      hideDeleteButton={hideDeleteButton}
    >
      <div
        ref={formInputRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role='button'
      >
        <FormInput
          {...rest}
          name={name}
          multiUpload
          type='attachment'
          onUpload={handleUpload}
          initialFileList={initialFileList.map((file) => ({
            id: file.hbDriveId,
            displayName: file.name,
            size: file.size,
            status: FileStatus.Success,
            initiallyUploaded: true,
            onDelete: () => handleDelete(file),
            uploadedAt: renderDate(file.createdAt),
            uploadedBy: file.addedBy
              ? {
                  label: file.addedBy.name,
                  value: file.addedBy.id,
                  initials: file.addedBy.initials ?? '',
                }
              : undefined,
          }))}
        />
      </div>
    </StyledStack>
  );
}
